import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { logError } from '@zg-rentals/log-error';
import { PaymentMethodIframe } from '@zillow/metroid-components';

import { EnvSelectors } from '../../../selectors';
import DispatchConstants from '../../../constants/DispatchConstants';

import type { MetroidAccountIdType } from '@zg-rentals/rental-manager-env';

type MetroidResponse<D> = {
  code: string | null;
  code_details: string | null;
  data: D | null;
};

type Props = {
  metroidAuthToken: string;
  businessUnit: string;
  listingAlias: string;
  zuoraUserToken: string;
};

const AlicornIframeWrapper = ({ metroidAuthToken, businessUnit, listingAlias, zuoraUserToken }: Props) => {
  const dispatch = useDispatch();
  const metroidAlicornUrl = useSelector((state) => EnvSelectors.makeMetroidAlicornUrl(state));
  const metroidAlicornHppConfigId = useSelector((state) => EnvSelectors.makeMetroidAlicornHppConfigId(state));

  const metroidAccountId: MetroidAccountIdType = {
    brand: 'Zillow',
    businessUnit,
    userId: zuoraUserToken,
  };

  useEffect(() => {
    return () => {
      dispatch({
        type: DispatchConstants.CREDIT_CARD_FORM_LOAD_COMPLETE,
        payload: false,
      });
    };
  }, [dispatch]);

  const onRenderComplete = (): void => {
    dispatch({
      type: DispatchConstants.CREDIT_CARD_FORM_LOAD_COMPLETE,
      payload: true,
    });
  };

  const onSubmitSuccess = (): void => {
    dispatch({
      type: DispatchConstants.VERIFY_CREDIT_CARD_CAPTURE_SUCCESS,
    });
  };

  const onSubmitFailure = (error: MetroidResponse<string>): void => {
    const isMaxAttemptsError = error?.code_details?.includes('Attempt_Exceed_Limitation');

    logError({
      error,
      context: {
        message: isMaxAttemptsError ? 'Exceded HPP Attempts' : 'Could not load credit card iframe.',
        details: 'AlicornIframeWrapper.onSubmitFailure',
        listingId: listingAlias,
      },
    });
    dispatch({
      type: DispatchConstants.CREDIT_CARD_FORM_SUBMIT_FAIL,
      payload: error,
    });
  };

  return (
    <PaymentMethodIframe
      elementId="alicorn-iframe"
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={onSubmitFailure}
      onRenderComplete={onRenderComplete}
      metroidAuthToken={metroidAuthToken}
      metroidHPPConfigId={metroidAlicornHppConfigId}
      metroidAccountId={metroidAccountId}
      metroidEndpointUrl={metroidAlicornUrl}
      setAsDefault={true}
    />
  );
};

export default AlicornIframeWrapper;
